import {
  useAccount,
  useEstimateGas as useWagmiEstimateGas,
  useFeeData,
} from 'wagmi';
import BigNumber from 'bignumber.js';

// Monad network constants
const MONAD_TRANSACTION_GAS_LIMIT = 30_000_000n; // Max gas limit per transaction
const MONAD_MIN_BASE_FEE = 100n; // 100 MON-gwei (100 * 10^-9 MON)
const GWEI = 1_000_000_000n; // 1 gwei in wei

// Known gas costs for common operations (as recommended by Monad docs)
const BASE_DEPOSIT_GAS = 200_000n;
const PER_CLAIM_GAS = 50_000n;

/**
 * Monad charges gas_limit (not gas_used) for transactions.
 * This hook provides improved gas estimation with:
 * - EIP-1559 compatible pricing (base fee + priority fee)
 * - Better fallback logic to detect wallet estimation failures
 * - Explicit gas limits for known operations
 */
export function useEstimateGas({
  encodedData,
  toAddress,
  value,
  claimCount = 0,
  enabled = true,
  // Optional: explicit gas limit for operations with known costs
  explicitGasLimit,
}: {
  encodedData?: `0x${string}`;
  toAddress: `0x${string}`;
  value?: bigint;
  claimCount?: number;
  enabled?: boolean;
  explicitGasLimit?: bigint;
}) {
  const { address } = useAccount();

  // Get EIP-1559 fee data (base fee + priority fee)
  const { data: feeData, isLoading: isLoadingFeeData } = useFeeData({
    query: {
      enabled: enabled,
      // Refetch every block to get current base fee
      refetchInterval: 12000, // ~12 seconds (Monad block time)
    },
  });

  // Estimate gas using RPC
  const {
    data: gasEstimate,
    isLoading: isLoadingGasEstimate,
    error: gasEstimateError,
  } = useWagmiEstimateGas({
    data: encodedData,
    to: toAddress,
    value,
    query: {
      enabled: !!address && !!encodedData && enabled && !explicitGasLimit,
    },
  });

  // Determine the gas limit to use
  // Priority: explicitGasLimit > validated estimate > fallback
  let gasLimit: bigint;
  let isUsingFallback = false;

  if (explicitGasLimit) {
    // Use explicit gas limit if provided (recommended by Monad for known operations)
    gasLimit = explicitGasLimit;
  } else if (gasEstimate && gasEstimate > 0n) {
    const gasEstimateBN = BigNumber(String(gasEstimate));

    // Validate gas estimate:
    // 1. Must be below Monad's transaction gas limit (30M)
    // 2. Must be reasonable (not suspiciously high, which indicates wallet failure)
    // Wallets like MetaMask set very high gas limits when estimation fails
    const isSuspiciouslyHigh = gasEstimateBN.gte(MONAD_TRANSACTION_GAS_LIMIT);
    const isValid =
      gasEstimateBN.gt(0) && gasEstimateBN.lt(MONAD_TRANSACTION_GAS_LIMIT);

    if (isValid && !isSuspiciouslyHigh) {
      gasLimit = gasEstimate;
    } else {
      // Fallback: use calculated estimate for multiclaim or base deposit
      isUsingFallback = true;
      gasLimit =
        claimCount > 0
          ? BigInt(BASE_DEPOSIT_GAS + BigInt(claimCount) * PER_CLAIM_GAS)
          : BASE_DEPOSIT_GAS;
    }
  } else {
    // No estimate available or estimation failed
    isUsingFallback = true;
    gasLimit =
      claimCount > 0
        ? BigInt(BASE_DEPOSIT_GAS + BigInt(claimCount) * PER_CLAIM_GAS)
        : BASE_DEPOSIT_GAS;
  }

  // Calculate transaction cost using EIP-1559 pricing
  // According to Monad docs: price_per_gas = min(base_fee + priority_fee, max_fee_per_gas)
  // transaction_cost = gas_limit * price_per_gas
  // Note: Monad charges gas_limit (not gas_used), so this is the actual cost
  let transactionCost = BigNumber(0);

  if (feeData) {
    // useFeeData returns recommended fees for EIP-1559:
    // - maxFeePerGas: recommended maximum total fee per gas (base + priority)
    // - maxPriorityFeePerGas: recommended priority fee
    // - gasPrice: current base fee estimate (fallback for legacy chains)

    // For cost estimation, use maxFeePerGas if available (it's the recommended total)
    // Otherwise, calculate base + priority from available fields
    let pricePerGas: bigint;

    if (feeData.maxFeePerGas) {
      // maxFeePerGas is the recommended total fee (already includes base + priority)
      pricePerGas = feeData.maxFeePerGas;
    } else if (feeData.gasPrice && feeData.maxPriorityFeePerGas) {
      // Calculate: base fee + priority fee
      pricePerGas = feeData.gasPrice + feeData.maxPriorityFeePerGas;
    } else if (feeData.gasPrice) {
      // Fallback: use gasPrice (base fee estimate) only
      pricePerGas = feeData.gasPrice;
    } else {
      // Last resort: use minimum base fee
      pricePerGas = MONAD_MIN_BASE_FEE * GWEI;
    }

    // Ensure minimum base fee (100 MON-gwei per Monad spec)
    const minPricePerGas = MONAD_MIN_BASE_FEE * GWEI;
    pricePerGas = pricePerGas < minPricePerGas ? minPricePerGas : pricePerGas;

    transactionCost = BigNumber(String(gasLimit)).times(String(pricePerGas));
  } else if (!isLoadingFeeData) {
    // Fee data not available and not loading - use minimum base fee as conservative estimate
    const fallbackPricePerGas = MONAD_MIN_BASE_FEE * GWEI;
    transactionCost = BigNumber(String(gasLimit)).times(
      String(fallbackPricePerGas)
    );
  }

  return {
    transactionCost,
    gasLimit,
    originalGasEstimate: gasEstimate,
    isUsingFallback,
    gasEstimateError,
    isLoadingTransactionCost: isLoadingGasEstimate || isLoadingFeeData,
    feeData: feeData
      ? {
          baseFee: feeData.gasPrice || feeData.maxFeePerGas,
          priorityFee: feeData.maxPriorityFeePerGas,
          maxFeePerGas: feeData.maxFeePerGas,
        }
      : undefined,
  };
}
